/**
 * Contenido de ayuda contextual para cada panel del BH Dashboard.
 * Cada entrada tiene: title, description, y sections (array de bloques explicativos).
 */

export const BH_HELP_CONTENT = {
  // ─── Location Selector ────────────────────────────────────────
  locationSelector: {
    title: "Location Selector",
    description:
      "Select a clinical location to load its Behavioral Health data. All panels update automatically when you change locations.",
    sections: [
      {
        heading: "How it works",
        items: [
          "Choose from 10 available clinical locations across Florida",
          "The number next to each location shows total BH patients",
          "Your last selection is remembered for your next visit",
          "All 7 dashboard panels refresh with the selected location's data",
        ],
      },
    ],
  },

  // ─── Panel 1: BH Overview ──────────────────────────────────────
  bhOverview: {
    title: "BH Population Overview",
    description:
      "High-level KPIs summarizing the behavioral health patient population at this location.",
    sections: [
      {
        heading: "KPI Cards",
        items: [
          "Total BH Patients (blue) -- Total behavioral health patients assigned to this location",
          "SPMI Patients (red) -- Patients with Serious & Persistent Mental Illness (AT RISK - SPMI + COMPLEX CHRONIC - SPMI)",
          "In Active CM (blue) -- BH patients currently enrolled in Care Management",
          "Unmanaged (red) -- BH patients without any active care management",
          "BH ER Events (amber) -- Patients with at least one BH-related ER visit",
          "New BH Members (cyan) -- Members who joined during this enrollment period",
        ],
      },
      {
        heading: "Subtitles",
        items: [
          "Total BH shows gender split and average age",
          "SPMI shows percentage of total BH population",
          "Active CM shows breakdown: Referred, Engaged, Attempted",
          "BH ER shows total events and frequent flyers (2+)",
        ],
      },
    ],
  },

  // ─── Panel 2: Risk Stratification ──────────────────────────────
  riskStratification: {
    title: "BH Risk Stratification",
    description:
      "Distribution of BH patients by behavioral health severity level. The donut chart shows the overall mix, and the cross-tab table reveals how BH severity intersects with general health status.",
    sections: [
      {
        heading: "BH Health Status Levels (7)",
        items: [
          "SEVERELY COMPLEX (dark red) -- Highest severity, multiple comorbidities",
          "COMPLEX CHRONIC - SPMI (red) -- Complex with serious mental illness",
          "COMPLEX CHRONIC - ANY BH (dark orange) -- Complex chronic behavioral health",
          "ACUTE (orange) -- Acute behavioral health conditions",
          "AT RISK - SPMI (amber) -- At risk with SPMI diagnosis",
          "AT RISK - ANY BH (blue) -- At risk for any behavioral health condition",
          "CHRONIC (green) -- Stable chronic behavioral health",
        ],
      },
      {
        heading: "Cross-Tab Table",
        items: [
          "Rows show BH Health Status, columns show General Health Status",
          "Identifies patients with BOTH high BH severity AND high medical complexity",
          "Patients in top-left corner (high BH + Complex/End of Life) need immediate attention",
        ],
      },
    ],
  },

  // ─── Panel 3: Substance Use ────────────────────────────────────
  substanceUse: {
    title: "Substance Use & Co-occurring",
    description:
      "Analysis of substance use disorders and behavioral comorbidities in the BH population.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "Drug Use Disorders -- Patients with any drug use disorder diagnosis",
          "Alcohol Use Disorders -- Patients with alcohol-related diagnoses",
          "Opioid Disorder -- Patients flagged for opioid use disorder",
          "Tobacco Use -- Patients with active tobacco use",
        ],
      },
      {
        heading: "Table",
        items: [
          "Shows patients with at least one SUD diagnosis",
          "Sorted by BH severity (highest first), then ER events",
          "SUD Type column shows all substance categories for each patient",
          "Action button creates an intervention task",
        ],
      },
      {
        heading: "Condition Bar Chart",
        items: [
          "Top BH conditions by prevalence in the population",
          "Includes both mental health and substance use conditions",
        ],
      },
    ],
  },

  // ─── Panel 4: BH ER Utilization ────────────────────────────────
  bhER: {
    title: "BH Inpatient & ER Utilization",
    description:
      "Behavioral health-related hospital and emergency events. Focuses on patients with BH-related ER visits.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "BH ER Patients -- Total patients with at least one BH ER event",
          "BH ER Frequent (2+) -- Patients with 2 or more BH ER events (high priority)",
          "BH Inpatient -- Patients currently in inpatient psychiatric care",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient -- Name of the patient",
          "BH ER Events -- Count of BH-related ER visits (red badge)",
          "BH Status / Health Status -- Severity classification chips",
          "Conditions -- Top conditions (hover for full list)",
          "LACE+ -- Readmission risk score when available",
          "Last ER -- Date and facility of most recent ER visit",
          "Action -- 'Schedule Follow-up' creates a task for post-ER contact",
        ],
      },
    ],
  },

  // ─── Panel 5: Unmanaged BH ─────────────────────────────────────
  unmanagedBH: {
    title: "Unmanaged BH Patients",
    description:
      "High-severity BH patients without active care management. This is the most actionable panel -- these patients need immediate CM enrollment.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "Severely Complex (no CM) -- Highest severity patients without care management",
          "Complex SPMI (no CM) -- Complex chronic SPMI patients without CM",
          "Complex Any BH (no CM) -- Complex chronic BH patients without CM",
        ],
      },
      {
        heading: "Table",
        items: [
          "Filtered to high-severity patients: Severely Complex, Complex Chronic, and Acute",
          "Sorted by BH severity DESC, then Global Indicator, then ER events",
          "Shows contact availability (phone, email) for outreach",
          "Action -- 'Enroll in CM' creates a care_plan task",
        ],
      },
    ],
  },

  // ─── Panel 6: Care Programs ────────────────────────────────────
  carePrograms: {
    title: "Care Programs & Engagement",
    description:
      "BH program enrollment status and patient outreach priorities.",
    sections: [
      {
        heading: "Program Cards",
        items: [
          "Digital Mental Well-Being -- Patients enrolled in digital wellness program",
          "Lucet BH Care -- Patients in the Lucet behavioral health care program",
          "No Program -- Patients not enrolled in any BH program (outreach targets)",
        ],
      },
      {
        heading: "Enrollment Table",
        items: [
          "Shows enrollment distribution by BH severity level",
          "Digital, Lucet, Both, and None columns",
          "Helps identify which severity levels lack program coverage",
        ],
      },
      {
        heading: "Outreach List",
        items: [
          "Patients without any program AND without CM",
          "Sorted by BH severity to prioritize highest-risk patients",
          "Shows available contact information (phone, email)",
          "Action -- 'Start Outreach' creates an outreach task",
        ],
      },
    ],
  },

  // ─── Panel 7: Tasks ────────────────────────────────────────────
  bhTasks: {
    title: "My Tasks",
    description:
      "Task management for BH care coordination. Tasks are created from action buttons across all panels.",
    sections: [
      {
        heading: "Task Sources",
        items: [
          "Panel 3 (SUD) -- 'Assign Intervention' creates clinical tasks",
          "Panel 4 (ER) -- 'Schedule Follow-up' creates follow-up tasks",
          "Panel 5 (Unmanaged) -- 'Enroll in CM' creates care plan tasks",
          "Panel 6 (Outreach) -- 'Start Outreach' creates outreach tasks",
          "Manual task creation via the + button",
        ],
      },
    ],
  },
};
